/* =====================================================================
 * WorkspaceFilesModal — 채팅 입력창 "+ → 파일 저장소 → <기능>" 에서 열리는
 * 워크스페이스 파일 브라우저 모달.
 *
 * 호스트가 `setSpecializedStorageComponents(<id>, { Tree, List })` 로 등록한
 * 전문 에이전트(CAD / 견적 등)의 저장소 Tree/List 컴포넌트를 그대로 재사용해
 * 해당 워크스페이스에 저장된 파일을 계층 폴더 구조로 보여준다. shared 패키지
 * 는 호스트 도메인 store 를 직접 모르므로, DI 레지스트리에서 컴포넌트를
 * 가져와 동일한 `SidebarSelection` 상태(좌측 트리 ↔ 우측 리스트)를 공유한다.
 *
 * - 등록된 컴포넌트가 없는 id 면 "준비 중" fallback.
 * - 모달이 열릴 때마다 선택을 해당 에이전트 루트로 초기화.
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { HardDrive, Loader, Sparkles, Upload } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authFetch, conversationToItem, listFiles, getFileServeUrl } from '@auw/api-client';
import { useChatStore } from '@auw/stores';
import type {
  FileAttachment,
  WorkspaceFile,
  WorkspaceMode,
} from '@auw/workspace-utils';
import { modeToPathWithId } from '@auw/workspace-utils';
import { Modal } from '../common/Modal';
import {
  getSpecializedStorageComponents,
  type PickedStorageFile,
} from '../../lib/specializedStorageSlot';
import { StorageFileList } from './StorageFileList';
import type { SidebarSelection } from './StorageSidebar';

interface WorkspaceFilesModalProps {
  open: boolean;
  onClose: () => void;
  /** 매니페스트 `specializedAgents[].id` (예: 'cad', 'estimate'). 전문 에이전트 저장소. */
  agentId?: string;
  /**
   * 코어 워크스페이스 모드(통합/라이브/문서). 지정되면 도메인 폴더 트리 대신
   * 공통 workspace_files 를 모드별 flat 리스트로 보여준다.
   */
  mode?: WorkspaceMode;
  /** 모달 헤더/좌측 레일에 표시할 사람이 읽을 수 있는 라벨. */
  agentLabel: string;
  /**
   * 체크한 파일들을 인풋 메시지에 카드로 첨부하는 콜백. 호스트(MessageInput)
   * 가 주입하면 우하단 "업로드" 버튼이 활성화된다.
   */
  onAttach?: (files: FileAttachment[]) => void;
}

const Split = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

const TreeRail = styled.aside`
  width: 248px;
  flex-shrink: 0;
  min-height: 0;
  overflow-y: auto;
  padding: 6px 6px 12px;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const RailAgentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin-bottom: 2px;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const RailAgentIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ListArea = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const Fallback = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
  padding: 40px;
  text-align: center;
`;

const HeaderSuffix = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* 본문(리스트) + 하단 액션 푸터를 세로로 쌓는 컬럼. */
const BodyColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/* 파일 체크 시 노출되는 우하단 업로드 바. */
const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const FooterInfo = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const UploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #fff;
  background: ${({ theme }) => theme.colors.brand};
  border: 1px solid ${({ theme }) => theme.colors.brandHover};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.28);
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};
  &:hover {
    background: ${({ theme }) => theme.colors.brandHover};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.34);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  svg.spin {
    animation: ${spin} 0.8s linear infinite;
  }
`;

function rootSelection(agentId: string): SidebarSelection {
  return {
    section: 'specialized',
    teamId: null,
    mode: null,
    specializedAgentId: agentId,
    specializedProjectId: null,
    specializedFolderId: null,
  };
}

/* ------------------------------------------------------------------ */
/* 코어 모드(통합/라이브/문서) flat 리스트                                */
/* ------------------------------------------------------------------ */

/* StoragePage 와 동일한 mode 판별 규칙(metadata.workspace_type 우선,
 * 없으면 source_type prefix 추론)을 로컬 복제. */
function inferModeFromSourceType(sourceType: string): WorkspaceMode | null {
  const st = (sourceType ?? '').toLowerCase();
  if (!st) return null;
  if (st === 'dm_upload' || st.startsWith('dm_')) return 'message';
  if (st.startsWith('doc-agent') || st.startsWith('doc_agent')) return 'doc-agent';
  if (st.startsWith('agent_')) return 'agent';
  if (st.endsWith('_upload') || st.endsWith('_output')) return 'agent';
  return null;
}

function fileBelongsToMode(file: WorkspaceFile, mode: WorkspaceMode): boolean {
  const meta = file.metadata as Record<string, unknown> | undefined;
  const raw = meta?.workspace_type;
  const wt = typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  if (wt) return wt === mode;
  return inferModeFromSourceType(file.source_type ?? '') === mode;
}

function getSourceItemId(file: WorkspaceFile): string | null {
  if (typeof file.source_item_id === 'string' && file.source_item_id.trim()) {
    return file.source_item_id;
  }
  const itemId = (file.metadata as Record<string, unknown> | undefined)?.item_id;
  return typeof itemId === 'string' && itemId.trim() ? itemId : null;
}

function wfToAttachment(wf: WorkspaceFile): FileAttachment {
  const serveUrl = getFileServeUrl(wf.file_id);
  return {
    id: wf.file_id,
    name: wf.original_name,
    type: wf.mime_type,
    size: wf.size_bytes,
    previewUrl: serveUrl,
    file: new File([], wf.original_name),
    remoteUrl: serveUrl,
  };
}

const ModeFilesView: React.FC<{
  mode: WorkspaceMode;
  onClose: () => void;
  onPickedChange: (files: readonly PickedStorageFile[]) => void;
}> = ({ mode, onClose, onPickedChange }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const setPreviewFile = useChatStore((s) => s.setPreviewFile);
  const conversations = useChatStore((s) => s.conversations);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const filesQuery = useQuery<WorkspaceFile[]>({
    queryKey: ['workspace-files-modal', 'mode', mode],
    queryFn: async () => {
      const res = await listFiles({ scope: 'personal', limit: 500 });
      return res.files ?? [];
    },
    staleTime: 30 * 1000,
  });

  // 파일 저장소 본 화면과 동일하게, 현재 프런트 workspace 목록에 실제로
  // 노출된 개인 대화 item에 연결된 파일만 보여준다. source_type만으로
  // 필터링하면 삭제/미노출 대화의 orphan 파일까지 picker에 나타난다.
  const visibleItemIds = useMemo(
    () =>
      new Set(
        Object.values(conversations)
          .map(conversationToItem)
          .filter((item) => item.mode === mode && item.scope === 'personal')
          .map((item) => item.item_id),
      ),
    [conversations, mode],
  );

  const files = useMemo(
    () =>
      (filesQuery.data ?? []).filter((file) => {
        const itemId = getSourceItemId(file);
        return !!itemId && visibleItemIds.has(itemId);
      }),
    [filesQuery.data, visibleItemIds],
  );

  const fileById = useMemo(
    () => new Map(files.map((f) => [f.file_id, f])),
    [files],
  );

  // 보이는 파일이 변하면 사라진 id 는 선택에서 제거.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(files.map((f) => f.file_id));
      const next = new Set<string>();
      for (const id of prev) if (visible.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [files]);

  // 선택 변경을 모달로 보고 (우하단 업로드 버튼 노출/첨부용).
  useEffect(() => {
    const picked = Array.from(selectedIds)
      .map((id) => fileById.get(id))
      .filter((f): f is WorkspaceFile => !!f)
      .map((f) => ({
        fileId: f.file_id,
        name: f.original_name,
        mimeType: f.mime_type,
        size: f.size_bytes,
      }));
    onPickedChange(picked);
  }, [selectedIds, fileById, onPickedChange]);

  const allSelected =
    files.length > 0 && files.every((f) => selectedIds.has(f.file_id));

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (files.length === 0) return prev;
      if (files.every((f) => prev.has(f.file_id))) return new Set();
      return new Set(files.map((f) => f.file_id));
    });
  }, [files]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (filesQuery.isLoading) {
    return <Fallback>{t('common.loading', { defaultValue: '불러오는 중...' })}</Fallback>;
  }
  if (files.length === 0) {
    return (
      <Fallback>
        {t('chat.workspaceFilesEmptyFiles', { defaultValue: '저장된 파일이 없습니다.' })}
      </Fallback>
    );
  }

  return (
    <ListArea>
      <StorageFileList
        files={files}
        selectedIds={selectedIds}
        allSelected={allSelected}
        onToggleSelect={toggleSelect}
        onSelectAll={handleSelectAll}
        onOpenFile={(wf) => {
          setPreviewFile(wfToAttachment(wf));
          onClose();
        }}
        onContextMenu={() => undefined}
        // 이 목록은 visibleItemIds와 연결된 파일만 남긴 결과이므로 모든 행의
        // 출처가 유효하다. 공통 리스트가 비활성 스타일로 오판하지 않게 명시한다.
        canOpenSource={() => true}
        onOpenSource={(file) => {
          const itemId = getSourceItemId(file);
          if (!itemId) return;
          navigate(modeToPathWithId(mode, itemId));
          onClose();
        }}
      />
    </ListArea>
  );
};

export const WorkspaceFilesModal: React.FC<WorkspaceFilesModalProps> = ({
  open,
  onClose,
  agentId,
  mode,
  agentLabel,
  onAttach,
}) => {
  const { t } = useTranslation('common');
  const addToast = useChatStore((s) => s.addToast);
  const [selection, setSelection] = useState<SidebarSelection>(() =>
    rootSelection(agentId ?? ''),
  );
  const [picked, setPicked] = useState<readonly PickedStorageFile[]>([]);
  const [uploading, setUploading] = useState(false);

  // 모달이 열릴 때(또는 다른 기능으로 바뀔 때) 선택을 루트로 초기화.
  useEffect(() => {
    if (open && agentId) setSelection(rootSelection(agentId));
  }, [open, agentId]);

  // 모달이 열리거나 소스(전문 에이전트/모드)가 바뀌면 픽 선택 초기화.
  useEffect(() => {
    setPicked([]);
  }, [open, agentId, mode]);

  const comps = useMemo(
    () => (agentId ? getSpecializedStorageComponents(agentId) : null),
    [agentId],
  );

  const Tree = comps?.Tree ?? null;
  const List = comps?.List ?? null;

  const handleUpload = useCallback(async () => {
    if (picked.length === 0 || !onAttach) {
      onClose();
      return;
    }
    setUploading(true);
    try {
      const attachments = await Promise.all(
        picked.map(async (p) => {
          const res = await authFetch(getFileServeUrl(p.fileId));
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const file = new File([blob], p.name, {
            type: p.mimeType || blob.type,
          });
          const isImage = (p.mimeType || blob.type).startsWith('image/');
          return {
            id: uuid(),
            name: p.name,
            type: p.mimeType || blob.type,
            size: file.size,
            previewUrl: isImage ? URL.createObjectURL(file) : undefined,
            file,
          } satisfies FileAttachment;
        }),
      );
      onAttach(attachments);
      setPicked([]);
      onClose();
    } catch {
      addToast?.({ type: 'error', title: '파일을 불러오지 못했습니다.' });
    } finally {
      setUploading(false);
    }
  }, [picked, onAttach, onClose, addToast]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('chat.workspaceFiles', { defaultValue: '파일 저장소' })}
      titleIcon={<HardDrive size={16} />}
      titleSuffix={
        agentLabel ? <HeaderSuffix>{agentLabel}</HeaderSuffix> : undefined
      }
      width="900px"
      defaultHeight={580}
      minWidth={560}
      minHeight={360}
      resizable
      bodyPadding="0"
      bodyOverflowY="hidden"
    >
      <BodyColumn>
        <ContentArea>
          {Tree && List ? (
            <Split>
              <TreeRail>
                <RailAgentRow>
                  <RailAgentIcon>
                    <Sparkles size={13} />
                  </RailAgentIcon>
                  {agentLabel}
                </RailAgentRow>
                <Tree selection={selection} onSelect={setSelection} />
              </TreeRail>
              <ListArea>
                <List
                  selection={selection}
                  onSelect={setSelection}
                  onPickedFilesChange={onAttach ? setPicked : undefined}
                />
              </ListArea>
            </Split>
          ) : mode ? (
            /* 모달이 닫혀 있을 때 불필요한 쿼리를 막기 위해 open 일 때만 마운트. */
            open ? (
              <ModeFilesView
                mode={mode}
                onClose={onClose}
                onPickedChange={setPicked}
              />
            ) : null
          ) : (
            <Fallback>
              {t('chat.workspaceFilesEmpty', { defaultValue: '준비 중입니다.' })}
            </Fallback>
          )}
        </ContentArea>
        {onAttach && picked.length > 0 && (
          <Footer>
            <FooterInfo>
              {t('storage.selectedCount', {
                defaultValue: '{{count}}개 선택',
                count: picked.length,
              })}
            </FooterInfo>
            <UploadButton type="button" onClick={handleUpload} disabled={uploading}>
              {uploading ? (
                <Loader size={14} className="spin" />
              ) : (
                <Upload size={14} />
              )}
              {t('chat.workspaceFilesUpload', { defaultValue: '업로드' })}
            </UploadButton>
          </Footer>
        )}
      </BodyColumn>
    </Modal>
  );
};

export default WorkspaceFilesModal;
